import Vue from 'vue'
import gql from 'graphql-tag'
import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'

import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Vehicle } from '@/modules/Vehicle/interfaces/vehicle'
import { showErrors } from '@/utils/snack'
import { dateTimeToDisplay } from '@/utils/dateTime'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppTextField from '@/components/AppTextField.vue'
import ContactDialog from '@/modules/Contact/scripts/contact-dialog'
import VehicleDialog from '@/modules/Vehicle/scripts/vehicle-dialog'
import { getVehicles } from '@/modules/Vehicle/gql/vehicle.gql'
import { getCaseById } from '@/modules/Case/gql/case.gql'
import { getPolicyById } from '@/modules/Policy/gql/policy.gql'
import { getQuote } from '@/modules/Quote/gql/quote.gql'
import { Case } from '@/modules/Case/interfaces/case'
import { Policy } from '@/modules/Policy/interfaces/policy'
import { Quote } from '@/modules/Quote/interfaces/quote'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppRefresh,
    AppTextField
  }
})
export default class VehicleList extends mixins(VehicleDialog, ContactDialog) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Vehicles'
    }
  ]
  searchVal = ''
  policyId: number | null = null
  quoteId: number | null = null
  pathType: string = ''

  policy: Policy | null= null
  quote: Quote | null = null
  caseInfo: Case | null = null

  vehicles: Vehicle[] = []

  tableColumn = [
    { field: 'name', title: 'Vehicle', slots: { default: 'name_default' } },
    { field: 'vin',
      title: 'VIN',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.vin ? row.vin : '-')
        ]
      }
      },
      resizable: true },
    { field: 'year',
      title: 'Year',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.year ? row.year : '-')
        ]
      }
      },
      resizable: true
    },
    { field: 'model',
      title: 'Model',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.model ? row.model : '-')
        ]
      }
      },
      resizable: true
    },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 90 }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'id',
    sortDesc: true
  }
  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID'
  }

  dateTimeToDisplay = dateTimeToDisplay

  mounted () {
    this.onResize()
    this.getList()
  }

  async getList () {
    const { params } = this.$route
    const { policyId, quoteId, caseId } = params

    if (policyId) {
      await this.getPolicy(+policyId)
    } else if (quoteId) {
      await this.getQuote(+quoteId)
    } else if (caseId) {
      await this.getCase(+caseId)
    }
    return this.getVehicles()
  }

  onResize () {
    const container = this.$refs.container as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  routeTo (name: string, params: any) {
    this.$router.push({
      name,
      params
    })
  }

  routeToDetail (item: any) {
    this.$router.push({
      name: 'VehicleDetailDetail',
      params: {
        id: item.id
      }
    })
  }

  async getCase (caseId: number) {
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getCaseById,
        variables: {
          caseId
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      if (this.breadcrumbs.length === 1) {
        this.breadcrumbs.unshift({
          text: `${result.data.getCaseById.inquiryName} (Case ID: ${result.data.getCaseById.id})`,
          to: {
            name: 'CaseDetailRelated',
            params: {
              id: result.data.getCaseById.id
            }
          }
        })
      }
      this.vehicles = result.data.getCaseById.vehicles
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = this.vehicles.length
      this.caseInfo = result.data.getCaseById
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getPolicy (policyId: number) {
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getPolicyById,
        variables: {
          policyId
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      if (this.breadcrumbs.length === 1) {
        this.breadcrumbs.unshift({
          text: `${result.data.getPolicyById.policyName} (Policy ID: ${result.data.getPolicyById.id})`,
          to: {
            name: 'PolicyDetailRelated',
            params: {
              id: result.data.getPolicyById.id
            }
          }
        })
      }
      this.vehicles = result.data.getPolicyById.vehicles
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = this.vehicles.length
      this.policy = result.data.getPolicyById
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getQuote (quoteId: number) {
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getQuote,
        variables: {
          quoteId
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      if (this.breadcrumbs.length === 1) {
        this.breadcrumbs.unshift({
          text: `${result.data.getQuote.name} (Quote ID: ${result.data.getQuote.id})`,
          to: {
            name: 'QuoteDetailRelated',
            params: {
              id: result.data.getQuote.id
            }
          }
        })
      }
      this.vehicles = result.data.getQuote.vehicles
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = this.vehicles.length
      this.quote = result.data.getQuote
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getVehicles () {
    const { sortBy, sortDesc, tablePage } = this.tableOptions
    const { currentPage, pageSize } = tablePage
    const searchVal = this.searchVal ? this.searchVal : null
    const orderBy = this.orderBy[sortBy] || 'ID'
    const { policy, quote, caseInfo } = this
    this.tableOptions.loading = true
    const filter: any = {
      name: searchVal
    }

    if (policy) {
      filter.policyId = +policy.id
    }
    if (quote) {
      filter.quoteId = +quote.id
    }
    if (caseInfo) {
      filter.caseId = +caseInfo.id
    }
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getVehicles,
        variables: {
          filter,
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.vehicles = result.data.getVehicles.vehicles
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = result.data.getVehicles.paging.totalCount
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async deleteVehicleConfirm () {
    await this.deleteVehiclePost()
    this.getList()
  }

  refreshList () {
    this.searchVal = ''
    this.getList()
  }

  sortTable (column: any) {
    this.tableOptions.sortBy = column.property
    this.tableOptions.sortDesc = column.order === 'desc'
    this.getList()
  }

  handlePageChange (pageData: any) {
    this.tableOptions.tablePage.currentPage = pageData.currentPage
    this.tableOptions.tablePage.pageSize = pageData.pageSize
    this.getList()
  }
}
